import type { WorkerMessage, MainThreadMessage } from './types';
import RendererWorker from './rendererWorker.ts?worker';
import { detectRendererCapabilities, canUseWebGL2Renderer } from './detection';
export { detectRendererCapabilities, canUseWebGL2Renderer };

export type RenderMode = 'webgl2-p3' | 'webgl2' | 'canvas2d';

export interface RendererBridgeCallbacks {
  onResult?: (bitmap: ImageBitmap, mode: RenderMode, frameTimeMs: number) => void;
  onError?: (reason: string) => void;
  onExport?: (blob: Blob) => void;
}

export class RendererBridge {
  private worker: Worker;
  private canvas: HTMLCanvasElement;
  private offscreen: OffscreenCanvas;
  private ready = false;
  private mode: RenderMode = 'canvas2d';
  private callbacks: RendererBridgeCallbacks;
  private readyPromise: Promise<void>;
  private resolveReady!: () => void;
  private pendingMakeup: Omit<Extract<WorkerMessage, { type: 'renderMakeup' }>, 'type'> | null = null;

  constructor(canvas: HTMLCanvasElement, callbacks: RendererBridgeCallbacks) {
    this.canvas = canvas;
    this.callbacks = callbacks;
    this.worker = new RendererWorker();
    this.offscreen = canvas.transferControlToOffscreen();
    this.readyPromise = new Promise<void>((resolve) => {
      this.resolveReady = resolve;
    });
    this.worker.onmessage = (ev: MessageEvent<MainThreadMessage>) => {
      const msg = ev.data;
      if (msg.type === 'ready') {
        this.ready = true;
        this.mode = msg.mode;
        this.resolveReady();
        if (this.pendingMakeup) {
          const queued = this.pendingMakeup;
          this.pendingMakeup = null;
          this.renderMakeup(queued);
        }
      } else if (msg.type === 'result') {
        this.callbacks.onResult?.(msg.bitmap, msg.mode, msg.frameTimeMs);
      } else if (msg.type === 'error') {
        this.callbacks.onError?.(msg.reason);
      } else if (msg.type === 'exportResult') {
        this.callbacks.onExport?.(msg.blob);
      }
    };
  }

  init(width: number, height: number, preferP3 = true): Promise<void> {
    const caps = detectRendererCapabilities();
    const colorSpace = preferP3 && caps.p3 && caps.halfFloat ? 'display-p3' : 'srgb';
    const msg: WorkerMessage = {
      type: 'init',
      canvas: this.offscreen,
      width,
      height,
      colorSpace
    };
    this.worker.postMessage(msg, [this.offscreen as unknown as Transferable]);
    return this.readyPromise;
  }

  whenReady(): Promise<void> {
    return this.readyPromise;
  }

  render(time: number): void {
    if (!this.ready) return;
    const msg: WorkerMessage = { type: 'render', width: this.canvas.width, height: this.canvas.height, time };
    this.worker.postMessage(msg);
  }

  renderSDF(vertices: Float32Array, width: number, height: number): void {
    if (!this.ready) return;
    const msg: WorkerMessage = { type: 'renderSDF', width, height, vertices };
    this.worker.postMessage(msg, [vertices.buffer]);
  }

  renderMakeup(params: Omit<Extract<WorkerMessage, { type: 'renderMakeup' }>, 'type'>): void {
    if (!this.ready) {
      // Queue the most recent request — replay once worker is ready.
      // Older queued requests are dropped: only the latest set of params matters.
      this.pendingMakeup = params;
      return;
    }
    const msg: WorkerMessage = { type: 'renderMakeup', ...params };
    const transfers: Transferable[] = [];
    // Transfer atlases (generated per-render) but NOT source bitmap
    // which is reused by the Canvas2D fallback path.
    if (msg.faceAtlas) transfers.push(msg.faceAtlas);
    if (msg.eyeAtlas) transfers.push(msg.eyeAtlas);
    if (msg.detailAtlas) transfers.push(msg.detailAtlas);
    if (msg.linerAtlas) transfers.push(msg.linerAtlas);
    if (msg.hairAtlas) transfers.push(msg.hairAtlas);
    this.worker.postMessage(msg, transfers);
  }

  exportImage(format: 'jpeg' | 'png' = 'jpeg', quality = 0.92): void {
    if (!this.ready) return;
    const msg: WorkerMessage = { type: 'export', format, quality };
    this.worker.postMessage(msg);
  }

  destroy(): void {
    this.worker.terminate();
  }

  getMode(): RenderMode {
    return this.mode;
  }
}
